import { ForbiddenError } from './errors';

function ApiManager(sessionToken, GSendpoint, errorCallback, maxRate = 10) {

    let subscriptionBucket;
    let server = "";
    let apiRequestTime = Date.now();
    let apiRequestCount = 0;
    let maxApiRateHit = 0;
    console.log("Max. permitted API rate: " + maxRate);// eslint-disable-line

    const basicHeaders = {
        "Accept": "application/ld+json",
        "Accept-encoding": "gzip, deflate"
    };

    this.getServer = function () {
        return server;
    };

    this.setSubscriptionBucket = function (bucket) {
        subscriptionBucket = bucket;
    };

    // If bucket is given we route the request to that server
    this.get = async function (endpoint, format = "application/json", codesToHandle) {
        return getExternalResponse(endpoint, {
            method: 'GET',
            headers: new Headers({ "Accept": format }),
            mode: 'cors',
            cache: 'default'
        }, codesToHandle);
    };

    // If bucket is given we route the request to that server
    this.getData = async function (endpoint, format = "application/ld+json", bucket, codesToHandle) {
        return getResponse(endpoint, GETrequestBuilder({
            "Accept": format,
            ...(bucket) && { "Graphologi-Target": bucket }
        }), codesToHandle);
    };

    this.deleteData = async function (endpoint) {
        return getResponse(endpoint, DELETErequestBuilder());
    };

    this.uploadData = async function (formData, projectIRI, graphIRI, subscription) {
        return getResponse(`/data?project=${projectIRI}&subscription=${subscription}`, POSTrequestBuilder({}, formData));
    };

    this.sparql = async function (endpoint, formData) {
        return getResponse(endpoint, POSTrequestBuilder({
            "Accept": "application/sparql-results+json"
        }, formData));
    };

    this.uploadProject = async function (formData, subscription, projectType) {
        return getResponse(`/project?type=${projectType}&subscription=${subscription}`, POSTrequestBuilder({}, formData));
    };

    this.uploadExampleProject = async function (id, subscription) {
        return getResponse(`/project/example/${id}?subscription=${subscription}`, POSTrequestBuilder({}));
    };

    this.uploadFeedback = async function (formData) {
        return getResponse(`/feedback`, POSTrequestBuilder({}, formData));
    };

    this.uploadStatus = async function (uuid, subscription, format = "application/ld+json") {
        return getResponse(`/data/${uuid}?subscription=${subscription}`, GETrequestBuilder({
            "Accept": format
        }));
    };

    this.sendData = async function (endpoint, payload, suppliedOptions) {
        let options = {
            format: "application/json",
            accept: "application/json"
        };
        const finalOptions = Object.assign(options, suppliedOptions);
        return getResponse(endpoint, POSTrequestBuilder({
            "Cache-Control": "no-cache",
            "Content-Type": finalOptions.format,
            "Accept": finalOptions.accept
        }, JSON.stringify(payload)));
    };

    this.updateData = async function (endpoint, payload, suppliedOptions) {
        let options = {
            format: "application/json",
            accept: "application/json"
        };
        const finalOptions = Object.assign(options, suppliedOptions);
        return getResponse(endpoint, PATCHrequestBuilder({
            "Cache-Control": "no-cache",
            "Content-Type": finalOptions.format,
            "Accept": finalOptions.accept
        }, JSON.stringify(payload)), [403]);
    };

    function headersBuilder(addHeaders) {
        let customHeaders = Object.assign({}, basicHeaders);
        customHeaders.Authorization = "Bearer " + sessionToken;
        if (subscriptionBucket) {
            customHeaders["Graphologi-Target"] = subscriptionBucket;
        }
        for (let property in addHeaders) {
            if (addHeaders.hasOwnProperty(property)) {
                customHeaders[property] = addHeaders[property];
            }
        }
        return new Headers(customHeaders);
    }

    function GETrequestBuilder(addHeaders) {
        return {
            method: 'GET',
            headers: headersBuilder(addHeaders),
            mode: 'cors',
            cache: 'default'
        };
    }

    function DELETErequestBuilder(addHeaders) {
        return {
            method: 'DELETE',
            headers: headersBuilder(addHeaders),
            mode: 'cors',
            cache: 'no-cache'
        };
    }

    function POSTrequestBuilder(addHeaders, bodyText) {
        return {
            method: 'POST',
            headers: headersBuilder(addHeaders),
            mode: 'cors',
            cache: 'no-cache',
            body: bodyText
        };
    }

    function PATCHrequestBuilder(addHeaders, bodyText) {
        return {
            method: 'PATCH',
            headers: headersBuilder(addHeaders),
            mode: 'cors',
            cache: 'no-cache',
            body: bodyText
        };
    }

    async function getExternalResponse(endpoint, reqHeaders, codesToHandle = []) {
        let res;
        try {
            res = await fetch(endpoint, reqHeaders);
            if (res.ok) return res;
            // We give JSON error responses but AWS might give something else so make sure we check
            const contentType = res.headers.get("content-type") || "";
            const responseFormat = contentType.split(";").map(value => value.trim());
            const errorData = responseFormat.includes("application/json") ? await res.json() : { message: "An unknown problem occurred." };
            if (res.status === 401 || res.status === 403) {
                throw new ForbiddenError(errorData.message);
            }
            else if (codesToHandle.includes(res.status)) {
                return res;
            }
            else throw new Error(errorData.message);
        }
        catch (e) {
            if (res && res.status && codesToHandle.includes(res.status)) {
                return res;
            }
            if (e.message === "Failed to fetch") {
                throw new Error("There seem to be connection issues.\nThe service you are trying to access may not be available.");
            }
            throw e;
        }
    }

    function sleepFor(t) {
        return new Promise(resolve => setTimeout(resolve, t));
    }

    // In some situations we want to handle certain codes rather than throw errors
    async function getResponse(endpoint, reqHeaders, codesToHandle = []) {
        let res;
        try {
            const currentTime = Date.now();
            // Reset count after 1 second
            if (currentTime > apiRequestTime + 1000) {
                apiRequestTime = currentTime;
                apiRequestCount = 0;
            }
            // If we are approaching rate limit pause
            if (apiRequestCount > maxRate - 3) {
                console.log("Pausing API request. Rate is: " + apiRequestCount);// eslint-disable-line
                await sleepFor(1000);
                // Try again after a period of time
                return getResponse(endpoint, reqHeaders, codesToHandle);
            }
            apiRequestCount++;
            if (apiRequestCount > maxApiRateHit) {
                maxApiRateHit = apiRequestCount;
                //console.log("Max. API rate: " + maxApiRateHit);// eslint-disable-line
            }
            res = await fetch(GSendpoint + endpoint, reqHeaders);
            if (res.ok || res.status === 415) {
                const gsServer = res.headers.get("gs-server");
                if (gsServer) {
                    if (server !== gsServer) {
                        console.log("Switch of server to use: " + gsServer);// eslint-disable-line
                    }
                    server = gsServer;
                }
                return res;
            }
            else {
                // We give JSON error responses but AWS might give something else so make sure we check
                const contentType = res.headers.get("content-type") || "";
                const responseFormat = contentType.split(";").map(value => value.trim());
                const errorData = responseFormat.includes("application/json") ? await res.json() : { message: "An unknown problem occurred." };
                if (res.status === 401 || res.status === 403) {
                    if (errorData.message === "jwt expired") {
                        errorCallback("Your session has expired. You need to log back in to continue.", true);
                    }
                    else throw new ForbiddenError(errorData.message);
                }
                else if (codesToHandle.includes(res.status)) {
                    return res;
                }
                else if (res.status === 500 && errorData.message === "Subscription has failed.") {
                    throw new Error("Unfortunately there has been an issue with processing an operation on this subscription.\nThis subscription will be temporarily suspended whilst support investigates.\nThey will automatically be notified.");
                }
                else if (res.status === 500 && errorData.message.includes("The payload is too large.")) {
                    throw new Error(errorData.message);
                }
                else if (res.status === 500 || res.status === 502 || res.status === 503 || res.status === 504) {
                    throw new Error("A system error occurred. Please try logging out and back in. If the problem persists please contact support.");
                }
                else throw new Error(errorData.message);
            }
        }
        catch (e) {
            if (res && res.status && codesToHandle.includes(res.status)) {
                return res;
            }
            throw e;
        }
    }
}

export default ApiManager;